import { useEffect, useRef, useState } from 'react';

// Diwali decorations for the Buy AC page (/daikin-ac-price-list-delhi) only.
//
//   <DiwaliLights />     a string of twinkling lights draped across the top of
//                        the banner. Place it inside a `relative` wrapper.
//   <DiwaliFireworks />  crackers bursting over the page: a short show on
//                        arrival, then an occasional burst, then it stops.
//
// Both switch themselves off after 10 Nov 2026 (India time) — no redeploy
// needed. They render nothing on the server, so the pre-rendered HTML and
// SEO are untouched, and they never block a tap (pointer-events: none).
// The page's own colours are not changed. Visitors who have asked their
// phone to reduce motion get still lights and no fireworks.

export const DIWALI_ENDS = new Date('2026-11-11T00:00:00+05:30');

function useFestive() {
  const [on, setOn] = useState(false);
  useEffect(() => { setOn(Date.now() < DIWALI_ENDS.getTime()); }, []);
  return on;
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ---------------------------------------------------------------- lights

const BULBS = ['#FBBF24', '#F97316', '#FDE68A', '#60A5FA', '#F472B6', '#FBBF24', '#FDE68A', '#34D399'];

export function DiwaliLights() {
  const on = useFestive();
  if (!on) return null;

  // Three drapes across the width; bulbs hang along each curve.
  const drapes = 3;
  const w = 1000;
  const seg = w / drapes;
  const sag = 34;
  const perDrape = 9;
  const bulbs = [];
  let wire = 'M0 4';
  for (let d = 0; d < drapes; d += 1) {
    const x0 = d * seg;
    wire += ` Q${x0 + seg / 2} ${4 + sag * 2} ${x0 + seg} 4`;
    for (let i = 1; i < perDrape; i += 1) {
      const t = i / perDrape;
      // Point on the quadratic curve (peak sag at the middle).
      const x = x0 + seg * t;
      const y = 4 + 2 * (sag * 2) * t * (1 - t);
      bulbs.push({ x, y, c: BULBS[bulbs.length % BULBS.length], k: bulbs.length });
    }
  }

  return (
    <div className="diwali-lights pointer-events-none absolute inset-x-0 z-10" aria-hidden="true">
      <svg viewBox={`0 0 ${w} 90`} preserveAspectRatio="none" className="h-full w-full">
        <path d={wire} fill="none" stroke="#374151" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
      {/* Bulbs as HTML so they stay round when the SVG stretches. */}
      {bulbs.map((b) => (
        <span
          key={b.k}
          className="diwali-bulb"
          style={{
            left: `${(b.x / w) * 100}%`,
            top: `${(b.y / 90) * 100}%`,
            '--c': b.c,
            animationDelay: `${(b.k % 5) * 0.35}s`,
          }}
        />
      ))}
      <style>{`
        /* Strung in the gap above the banner, dipping only a little onto it,
           so the banner's own words stay clear. */
        .diwali-lights { top: -15px; height: 1.75rem; }
        @media (min-width: 640px) { .diwali-lights { top: -22px; height: 2.75rem; } }
        .diwali-lights svg { position: absolute; inset: 0; }
        .diwali-bulb {
          position: absolute; width: 7px; height: 10px; margin-left: -3.5px;
          border-radius: 50% 50% 50% 50% / 40% 40% 60% 60%;
          background: var(--c);
          box-shadow: 0 0 6px 2px var(--c);
          animation: diwali-twinkle 1.8s ease-in-out infinite;
        }
        @media (min-width: 640px) { .diwali-bulb { width: 9px; height: 13px; margin-left: -4.5px; } }
        @keyframes diwali-twinkle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; box-shadow: 0 0 2px 0 var(--c); }
        }
        @media (prefers-reduced-motion: reduce) { .diwali-bulb { animation: none; } }
      `}</style>
    </div>
  );
}

// ------------------------------------------------------------- fireworks

const SPARK = ['#FBBF24', '#FDE68A', '#F97316', '#60A5FA', '#FFFFFF', '#F472B6', '#34D399'];
const OPENING_BURSTS = 6;      // the arrival show
const LATER_EVERY_MS = 9000;   // then one now and then
const STOP_AFTER_MS = 60000;   // and nothing after a minute

export function DiwaliFireworks() {
  const on = useFestive();
  const ref = useRef(null);

  useEffect(() => {
    if (!on || reducedMotion()) return undefined;
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;
    const size = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    window.addEventListener('resize', size);

    const rockets = [];
    const sparks = [];
    const started = performance.now();
    let raf = 0;
    let timers = [];

    const launch = () => {
      if (document.hidden) return;
      const x = W * (0.15 + Math.random() * 0.7);
      const peak = H * (0.15 + Math.random() * 0.3);
      rockets.push({ x, y: H, vy: -(Math.sqrt(2 * 0.12 * (H - peak))), peak, c: SPARK[Math.floor(Math.random() * SPARK.length)] });
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const burst = (x, y) => {
      const n = W < 640 ? 46 : 70;
      const c1 = SPARK[Math.floor(Math.random() * SPARK.length)];
      const c2 = SPARK[Math.floor(Math.random() * SPARK.length)];
      const speed = W < 640 ? 2.6 : 3.4;
      for (let i = 0; i < n; i += 1) {
        const a = (Math.PI * 2 * i) / n;
        const s = speed * (0.6 + Math.random() * 0.5);
        sparks.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, c: i % 2 ? c1 : c2 });
      }
    };

    function tick() {
      ctx.clearRect(0, 0, W, H);
      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i];
        r.y += r.vy;
        r.vy += 0.12;
        ctx.fillStyle = r.c;
        ctx.globalAlpha = 0.9;
        ctx.fillRect(r.x - 1, r.y, 2, 6);
        if (r.vy >= -0.5 || r.y <= r.peak) { burst(r.x, r.y); rockets.splice(i, 1); }
      }
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const p = sparks[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + 0.035;
        p.life -= 0.012;
        if (p.life <= 0) { sparks.splice(i, 1); continue; }
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.fillStyle = p.c;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (rockets.length || sparks.length) raf = requestAnimationFrame(tick);
      else { raf = 0; ctx.clearRect(0, 0, W, H); }
    }

    // Arrival show, staggered.
    for (let i = 0; i < OPENING_BURSTS; i += 1) {
      timers.push(setTimeout(launch, 400 + i * 650 + Math.random() * 250));
    }
    // Occasional bursts, for the first minute only.
    const later = setInterval(() => {
      if (performance.now() - started > STOP_AFTER_MS) { clearInterval(later); return; }
      launch();
      timers.push(setTimeout(launch, 350));
    }, LATER_EVERY_MS);

    return () => {
      timers.forEach(clearTimeout);
      timers = [];
      clearInterval(later);
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
    };
  }, [on]);

  if (!on) return null;
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-40 h-full w-full"
    />
  );
}
